import { Controller } from '@hotwired/stimulus'

// Turbo disables a form's submit button while the request is in flight, but it
// offers no such protection for `data-turbo-method` links. Without it, a quick
// double/triple click (or an impatient click before the response renders) sends
// several requests and creates duplicate records. This controller locks the
// element after the first activation and releases it once its own request
// completes (or after a safety cooldown).
//
// Correlation note: Turbo turns a `data-turbo-method` link into a throwaway
// <form> appended to the <body> and submitted on the next animation frame, so
// the link itself is never the submission's `submitter`. We therefore correlate
// this element to its submission by matching the synthesized form's `action`
// against the link's URL (Turbo moves the query string into hidden inputs, so we
// compare without it), capture that exact `formSubmission` object on
// `turbo:submit-start`, and only unlock when *that* submission ends. Listening
// for any document-wide `turbo:submit-end` instead would let unrelated
// background auto-submits (details/line-items/investments/payment forms on the
// quotation edit page) release the lock between two rapid clicks.
export default class extends Controller {
  static values = {
    cooldown: { type: Number, default: 2000 }
  }

  guard (event) {
    if (this.locked) {
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }

    this.lock()
  }

  lock () {
    this.locked = true
    this.formSubmission = null

    this.boundMatchSubmission = this.matchSubmission.bind(this)
    this.boundUnlockOnSubmissionEnd = this.unlockOnSubmissionEnd.bind(this)

    document.addEventListener('turbo:submit-start', this.boundMatchSubmission)
    document.addEventListener('turbo:submit-end', this.boundUnlockOnSubmissionEnd)

    // Safety net: release the lock even if we never correlate a submission
    // (e.g. the element is not a link, or the request never fires).
    this.timeout = setTimeout(() => this.unlock(), this.cooldownValue)
  }

  matchSubmission (event) {
    if (this.formSubmission) return

    const submission = event.detail.formSubmission
    if (submission && this.submissionBelongsToElement(submission)) {
      this.formSubmission = submission
      document.removeEventListener('turbo:submit-start', this.boundMatchSubmission)
      this.boundMatchSubmission = null
    }
  }

  unlockOnSubmissionEnd (event) {
    if (this.formSubmission && event.detail.formSubmission === this.formSubmission) {
      this.unlock()
    }
  }

  unlock () {
    this.locked = false
    this.formSubmission = null
    clearTimeout(this.timeout)

    if (this.boundMatchSubmission) {
      document.removeEventListener('turbo:submit-start', this.boundMatchSubmission)
      this.boundMatchSubmission = null
    }

    if (this.boundUnlockOnSubmissionEnd) {
      document.removeEventListener('turbo:submit-end', this.boundUnlockOnSubmissionEnd)
      this.boundUnlockOnSubmissionEnd = null
    }
  }

  submissionBelongsToElement (submission) {
    const action = submission.formElement?.action
    if (!action || !this.element.href) return false

    const linkUrl = new URL(this.element.href, document.baseURI)
    linkUrl.search = ''
    return action === linkUrl.href
  }

  disconnect () {
    this.unlock()
  }
}
